import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { AuthContext, initialState } from "./auth-context";
import { authReducer } from "./reducer";
import { queryClient } from "../../config/query-client";
import { LocalStorageOperations } from "../../data/storage/LocalStorageOperations";
import { User } from "@core/types";
import { authClient } from "@core/clients";
import { ActionType } from "./types";
import WooComAPI from "../../providers/WooComAPI/woo-com-api";
import { client } from "../../apollo";
import { useOrderState } from "../../providers/orderDataStore";
import { useCustomerAddressesStorageState } from "../../providers/customerAddressesStorage/useCustomerAddressesStorage";
import { AppMetricaProvider } from "../../providers/AppMetrica/AppMetrica";
import AuthLoadingSkeleton from "./auth-loading-skeleton";

type authContextInstanceType = {
  isReady: boolean;
  dispatch: (v: ActionType) => void;
  logout: () => void;
};

export const authContextInstance: authContextInstanceType = {
  isReady: false,
  dispatch: () => new Error("Dispatch is not ready"),
  logout: () => {},
};

const AuthProvider = (props: any) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [isLoadingFromLocalStorage, setIsLoadingFromLocalStorage] = useState(true);

  const login = useCallback(
    async (user: User) => {
      dispatch({
        type: "LOGIN",
        payload: {
          user,
        },
      });
      await authClient.setToken(user.token, user.supabaseAuth);
      LocalStorageOperations.saveUserInfo(user);
      WooComAPI.setAccessToken(user.token);

      AppMetricaProvider.reportEvent("login", {
        user,
      });
    },
    [dispatch],
  );

  const updateUserData = useCallback((user: Partial<User>) => {
    dispatch({
      type: "UPDATE_USER_DATA",
      payload: {
        user,
      },
    });

    LocalStorageOperations.updateUserInfo(user || {});
  }, []);

  const logout = useCallback(
    async (revokeAccessToken = false) => {
      dispatch({
        type: "LOGOUT",
      });

      // Clearing cache so that one authorized user
      // will not see data from previously authorized user
      authClient.logout();
      WooComAPI.setAccessToken("");

      await queryClient.clear();
      await client.clearStore();
      useOrderState.getState().reset();
      useCustomerAddressesStorageState.getState().resetAuthedCustomerAddresses();
      useCustomerAddressesStorageState.persist.clearStorage();
      LocalStorageOperations.removeUserInfo();
    },
    [dispatch],
  );

  useEffect(() => {
    const initialize = async () => {
      try {
        const user = await LocalStorageOperations.getUserData();
        if (user) {
          await login(user);
          await authClient.initAuth();

        }
      } catch (err) {
        console.error("Failed to initialize auth in AuthProvider:", err);
      } finally {
        setIsLoadingFromLocalStorage(false);
      }
    };
    initialize();
  }, [login]);

  const value = useMemo(() => {
    return {
      ...state,
      login,
      logout,
      updateUserData,
    };
  }, [state, login, logout, updateUserData]);

  if (!authContextInstance.isReady) {
    authContextInstance.isReady = true;
    authContextInstance.dispatch = (params: ActionType) => dispatch(params);
    authContextInstance.logout = logout;
  }
  if (isLoadingFromLocalStorage) {
    return <AuthLoadingSkeleton />;
  }

  return <AuthContext.Provider value={value} {...props} />;
};
const useAuth = () => React.useContext(AuthContext);

export { AuthProvider, useAuth };
